/**
 * Real price of the SELECTED stay, read from the pricing breakdown.
 *
 * The availability grid (`POST /v1/inventory/grid`) deliberately returns the
 * normal nightly rate WITHOUT length-of-stay modifiers, so once dates are
 * selected the page must switch to `breakdown.base_rental`, which
 * `POST /v1/quotes/calculate-pricing` computes for exactly that stay.
 *
 * Only `base_rental` is read: add-ons, insurance, delivery, taxes and fees
 * never leak into a nightly number.
 */
import type { PricingBreakdown } from "@fm/api";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function nightsBetween(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  return Math.round((end - start) / 86_400_000);
}

interface StayBaseRental {
  total: number;
  days: number;
  dailyRates: Array<{ date: string; amount: string }>;
}

/**
 * Returns the base rental when it demonstrably belongs to `from`..`to`.
 * A breakdown left over from a previous date range (the page keeps it while
 * the next one loads) must never be shown against the new selection, so the
 * stay length and every dated night are checked against the range.
 */
function readStayBaseRental(
  breakdown: PricingBreakdown | null | undefined,
  from: string | undefined,
  to: string | undefined,
): StayBaseRental | null {
  if (!breakdown || !from || !to) return null;
  if (!ISO_DATE.test(from) || !ISO_DATE.test(to)) return null;

  const nights = nightsBetween(from, to);
  if (nights <= 0) return null;

  const baseRental = breakdown.breakdown?.base_rental;
  if (!baseRental) return null;

  const days = baseRental.days;
  if (typeof days !== "number" || days !== nights) return null;

  const total = parseFloat(baseRental.total);
  if (!Number.isFinite(total)) return null;

  const dailyRates = Array.isArray(baseRental.daily_rates)
    ? baseRental.daily_rates
    : [];
  const insideRange = dailyRates.every(
    (rate) =>
      typeof rate?.date === "string" && rate.date >= from && rate.date < to,
  );
  if (!insideRange) return null;

  return { total, days, dailyRates };
}

/**
 * Effective nightly rate of the stay (`base_rental.total / base_rental.days`),
 * or null when it cannot be trusted and the caller should keep the grid rate.
 * Differing per-date rates (weekend, seasonal) yield their average.
 */
export function getStayNightlyRate(
  breakdown: PricingBreakdown | null | undefined,
  from: string | undefined,
  to: string | undefined,
): number | null {
  const stay = readStayBaseRental(breakdown, from, to);
  if (!stay) return null;
  const rate = stay.total / stay.days;
  return Number.isFinite(rate) && rate > 0 ? rate : null;
}

/**
 * Per-night amounts of the stay keyed by YYYY-MM-DD, or null when the
 * breakdown does not belong to the range. Nights whose amount is not a finite
 * number are skipped so the caller falls back to the grid rate for them.
 */
export function getStayDailyRates(
  breakdown: PricingBreakdown | null | undefined,
  from: string | undefined,
  to: string | undefined,
): Map<string, number> | null {
  const stay = readStayBaseRental(breakdown, from, to);
  if (!stay) return null;

  const rates = new Map<string, number>();
  for (const { date, amount } of stay.dailyRates) {
    const value = parseFloat(amount);
    if (Number.isFinite(value)) rates.set(date, value);
  }
  return rates;
}
